import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import type { Showcase } from '../api';
import { buildBotFlow } from '../constants';
import { BOT_OPERATION, BOT_OPTION_TYPE, type BotFlowState, type MenuOption } from '../types';

interface ConversationTurn {
  /** Chave estavel da lista - a mesma frase pode se repetir na conversa. */
  id: string;
  type: 'bot' | 'user';
  text?: string;
  label?: string;
}

type BookingStep = 'service' | 'day' | 'time' | 'professional' | 'confirmation';

interface BookingJourneyState {
  /** Nulo = o cliente pediu para ver todos os servicos. */
  categoryId: string | null;
  step: BookingStep;
  service?: string;
  day?: string;
  time?: string;
  professional?: string;
  visibleTimesCount?: number;
}

const capitalizeFirst = (value: string) =>
  value.length > 0 ? value.charAt(0).toUpperCase() + value.slice(1) : value;

const getNextSevenDaysLabels = () => {
  const weekdayFormatter = new Intl.DateTimeFormat('pt-BR', { weekday: 'long' });
  const monthFormatter = new Intl.DateTimeFormat('pt-BR', { month: 'long' });
  const baseDate = new Date();

  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(baseDate);
    date.setDate(baseDate.getDate() + index);

    const weekday = capitalizeFirst(weekdayFormatter.format(date));
    const month = capitalizeFirst(monthFormatter.format(date));

    return `${weekday}, ${date.getDate()} de ${month}`;
  });
};

const getHalfHourTimeSlots = () => {
  const slots: string[] = [];

  for (let hour = 9; hour <= 22; hour += 1) {
    const hourLabel = hour.toString().padStart(2, '0');
    slots.push(`${hourLabel}:00`);

    if (hour < 22) slots.push(`${hourLabel}:30`);
  }

  return slots;
};

const initials = (name: string) => name.slice(0, 2).toLowerCase();

/**
 * Demonstracao do bot de agendamento no WhatsApp. Categorias, servicos e
 * profissionais vem da vitrine publica - o mesmo cadastro que a agenda usa.
 */
export function WhatsAppEmulator({ showcase }: { showcase: Showcase | undefined }) {
  const flowState: BotFlowState = buildBotFlow(showcase);

  const [currentMenuRef, setCurrentMenuRef] = useState<string | null>(flowState.initialMenuRef);
  const [conversation, setConversation] = useState<ConversationTurn[]>([]);
  const [bookingJourney, setBookingJourney] = useState<BookingJourneyState | null>(null);
  const chatScrollRef = useRef<HTMLDivElement | null>(null);

  const allServices = (showcase?.categories ?? []).flatMap((category) => category.services);
  const professionals = (showcase?.professionals ?? []).map((professional) => professional.name);

  const servicesOf = (categoryId: string | null) =>
    categoryId === null
      ? allServices
      : (showcase?.categories.find((category) => category.id === categoryId)?.services ?? []);

  const categoryName = (categoryId: string | null) =>
    categoryId === null
      ? 'Todos os servicos'
      : (showcase?.categories.find((category) => category.id === categoryId)?.name ?? '');

  const handleInitialize = () => {
    const startMenu = flowState.menus.find((menu) => menu.ref === flowState.initialMenuRef);
    if (startMenu) {
      setCurrentMenuRef(startMenu.ref);
      setBookingJourney(null);
      setConversation([{ id: crypto.randomUUID(), type: 'bot', text: startMenu.question }]);
    }
  };

  const getOperationResponse = (operation: MenuOption['operation']) => {
    switch (operation) {
      case BOT_OPERATION.LIST_AVAILABLE_TIMES:
        return 'Horarios disponiveis hoje: 10:00, 11:30, 14:00 e 16:30.';
      case BOT_OPERATION.LIST_AVAILABLE_PROFESSIONALS:
        return `Profissionais disponiveis: ${professionals.join(', ')}.`;
      case BOT_OPERATION.FINISH_SCHEDULING:
        return 'Perfeito! Vamos finalizar seu agendamento. Informe seu nome completo.';
      case BOT_OPERATION.CANCEL_SCHEDULING:
        return 'Tudo bem. Para cancelar, informe o numero do seu agendamento.';
      default:
        return 'Operacao executada com sucesso.';
    }
  };

  const getBookingOptions = (journey: BookingJourneyState) => {
    switch (journey.step) {
      case 'service':
        return servicesOf(journey.categoryId).map(
          (service) =>
            `${service.name} - ${service.price.toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            })}`,
        );
      case 'day':
        return getNextSevenDaysLabels();
      case 'time': {
        const allTimeSlots = getHalfHourTimeSlots();
        const visibleCount = Math.min(journey.visibleTimesCount ?? 6, allTimeSlots.length);
        const visibleSlots = allTimeSlots.slice(0, visibleCount);

        if (visibleCount < allTimeSlots.length) return [...visibleSlots, 'Ver mais horarios'];

        return visibleSlots;
      }
      case 'professional':
        return professionals;
      case 'confirmation':
        return ['Confirmar agendamento', 'Voltar ao inicio'];
      default:
        return [];
    }
  };

  const startBookingJourney = (categoryId: string | null, newConversation: ConversationTurn[]) => {
    setBookingJourney({ categoryId, step: 'service' });
    setConversation([
      ...newConversation,
      {
        id: crypto.randomUUID(),
        type: 'bot',
        text:
          categoryId === null
            ? 'Escolha um servico:'
            : `Escolha um servico de ${categoryName(categoryId).toLowerCase()}:`,
      },
    ]);
  };

  const handleBookingChoiceClick = (choice: string) => {
    if (!bookingJourney) return;

    const nextConversation: ConversationTurn[] = [
      ...conversation,
      { id: crypto.randomUUID(), type: 'user', label: choice },
    ];

    if (bookingJourney.step === 'service') {
      setBookingJourney({ ...bookingJourney, service: choice, step: 'day' });
      setConversation([
        ...nextConversation,
        { id: crypto.randomUUID(), type: 'bot', text: 'Escolha um dia' },
      ]);
      return;
    }

    if (bookingJourney.step === 'day') {
      setBookingJourney({ ...bookingJourney, day: choice, step: 'time', visibleTimesCount: 6 });
      setConversation([
        ...nextConversation,
        { id: crypto.randomUUID(), type: 'bot', text: `Escolha um horario de ${choice}:` },
      ]);
      return;
    }

    if (bookingJourney.step === 'time') {
      if (choice === 'Ver mais horarios') {
        setBookingJourney({
          ...bookingJourney,
          visibleTimesCount: (bookingJourney.visibleTimesCount ?? 6) + 6,
        });
        return;
      }

      setBookingJourney({ ...bookingJourney, time: choice, step: 'professional' });
      setConversation([
        ...nextConversation,
        { id: crypto.randomUUID(), type: 'bot', text: 'Escolha um profissional' },
      ]);
      return;
    }

    if (bookingJourney.step === 'professional') {
      const nextJourney: BookingJourneyState = {
        ...bookingJourney,
        professional: choice,
        step: 'confirmation',
      };

      setBookingJourney(nextJourney);
      setConversation([
        ...nextConversation,
        {
          id: crypto.randomUUID(),
          type: 'bot',
          text:
            'Resumo do agendamento:\n' +
            `Categoria: ${categoryName(nextJourney.categoryId)}\n` +
            `Servico: ${nextJourney.service}\n` +
            `Dia: ${nextJourney.day}\n` +
            `Horario: ${nextJourney.time}\n` +
            `Profissional: ${nextJourney.professional}\n\n` +
            'Deseja confirmar?',
        },
      ]);
      return;
    }

    if (bookingJourney.step === 'confirmation') {
      if (choice === 'Confirmar agendamento') {
        setBookingJourney(null);
        setCurrentMenuRef(null);
        setConversation([
          ...nextConversation,
          { id: crypto.randomUUID(), type: 'bot', text: 'Agendamento confirmado com sucesso!' },
        ]);
        return;
      }

      const startMenu = flowState.menus.find((menu) => menu.ref === flowState.initialMenuRef);

      setBookingJourney(null);
      setCurrentMenuRef(flowState.initialMenuRef);
      setConversation([
        ...nextConversation,
        ...(startMenu
          ? [{ id: crypto.randomUUID(), type: 'bot' as const, text: startMenu.question }]
          : []),
      ]);
    }
  };

  const handleOptionClick = (option: MenuOption) => {
    const newConversation: ConversationTurn[] = [
      ...conversation,
      { id: crypto.randomUUID(), type: 'user', label: option.label },
    ];

    if (option.type === BOT_OPTION_TYPE.OPERATION) {
      if (
        option.operation === BOT_OPERATION.LIST_CATEGORY_SERVICES ||
        option.operation === BOT_OPERATION.LIST_ALL_SERVICES
      ) {
        startBookingJourney(option.categoryId ?? null, newConversation);
        return;
      }

      newConversation.push({
        id: crypto.randomUUID(),
        type: 'bot',
        text: getOperationResponse(option.operation),
      });
      setConversation(newConversation);
      return;
    }

    if (option.nextMenuRef) {
      const nextMenu = flowState.menus.find((menu) => menu.ref === option.nextMenuRef);
      if (nextMenu) {
        newConversation.push({ id: crypto.randomUUID(), type: 'bot', text: nextMenu.question });
        setConversation(newConversation);
        setCurrentMenuRef(option.nextMenuRef);
        return;
      }
    }

    setConversation(newConversation);
    setCurrentMenuRef(null);
  };

  const currentMenu = flowState.menus.find((menu) => menu.ref === currentMenuRef);
  const hasBackToMainOption =
    currentMenu?.options.some(
      (option) =>
        option.type === BOT_OPTION_TYPE.SUBMENU && option.nextMenuRef === flowState.initialMenuRef,
    ) ?? false;

  const optionsWithBackToMain: MenuOption[] =
    currentMenu && currentMenu.ref !== flowState.initialMenuRef && !hasBackToMainOption
      ? [
          ...currentMenu.options,
          {
            label: 'Voltar ao menu principal',
            type: BOT_OPTION_TYPE.SUBMENU,
            nextMenuRef: flowState.initialMenuRef,
            operation: null,
          },
        ]
      : (currentMenu?.options ?? []);

  const bookingOptions = bookingJourney ? getBookingOptions(bookingJourney) : [];

  // biome-ignore lint/correctness/useExhaustiveDependencies: a conversa e o menu atual sao o gatilho da rolagem, nao valores lidos dentro do efeito.
  useEffect(() => {
    const chatContainer = chatScrollRef.current;
    if (!chatContainer) return;

    chatContainer.scrollTo({ top: chatContainer.scrollHeight, behavior: 'smooth' });
  }, [conversation, currentMenuRef]);

  const companyName = showcase?.company.name ?? 'Seu salao';

  return (
    <div className="flex h-full w-full items-center justify-center bg-transparent">
      <div className="relative h-full w-full max-w-[390px] rounded-[42px] bg-neutral-900 p-[10px] shadow-[0_24px_60px_rgba(0,0,0,0.42)]">
        <div className="absolute left-[-3px] top-28 h-10 w-1 rounded-l-full bg-neutral-700/70" />
        <div className="absolute left-[-3px] top-44 h-16 w-1 rounded-l-full bg-neutral-700/70" />
        <div className="absolute right-[-3px] top-36 h-20 w-1 rounded-r-full bg-neutral-700/70" />

        <div className="relative flex h-full flex-col overflow-hidden rounded-[34px] border border-black/30 bg-[#0b141a]">
          <div className="pointer-events-none absolute left-1/2 top-2 h-6 w-28 -translate-x-1/2 rounded-full bg-black/85" />

          <div className="mt-6 flex items-center gap-3 border-b border-white/10 bg-[#111b21] px-4 py-3 text-white">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500 text-[10px] font-bold uppercase tracking-wide text-white">
              {initials(companyName)}
            </div>
            <div>
              <div className="text-sm font-semibold">{companyName}</div>
              <div className="text-xs text-emerald-300">Online</div>
            </div>
          </div>

          <div
            ref={chatScrollRef}
            className="flex-1 space-y-3 overflow-y-auto bg-[#0b141a] bg-[radial-gradient(circle_at_top_left,rgba(22,50,58,0.6),transparent_45%)] p-4 [scrollbar-color:rgba(134,150,160,0.6)_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#8696a0]/60 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1 hover:[&::-webkit-scrollbar-thumb]:bg-[#8696a0]/80"
          >
            {conversation.length === 0 ? (
              <div className="flex h-full items-center justify-center text-center">
                <button
                  type="button"
                  onClick={handleInitialize}
                  className="rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-400"
                >
                  Iniciar conversa
                </button>
              </div>
            ) : (
              <>
                {conversation.map((turn) => (
                  <div
                    key={turn.id}
                    className={cn('flex', turn.type === 'bot' ? 'justify-start' : 'justify-end')}
                  >
                    <div
                      className={cn(
                        'max-w-[85%] whitespace-pre-line rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed shadow-sm',
                        turn.type === 'bot'
                          ? 'rounded-tl-sm bg-[#202c33] text-[#e9edef]'
                          : 'rounded-tr-sm bg-[#005c4b] text-white',
                      )}
                    >
                      {turn.text || turn.label}
                    </div>
                  </div>
                ))}

                {bookingJourney && bookingOptions.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {bookingOptions.map((optionLabel) => (
                      <button
                        type="button"
                        key={`${bookingJourney.step}-${optionLabel}`}
                        onClick={() => handleBookingChoiceClick(optionLabel)}
                        className="w-full rounded-xl border border-emerald-400/60 bg-[#111b21] px-4 py-2.5 text-left text-xs font-medium text-emerald-300 transition-colors hover:border-emerald-300 hover:bg-emerald-400/10 hover:text-emerald-200"
                      >
                        {optionLabel}
                      </button>
                    ))}
                  </div>
                )}

                {!bookingJourney && currentMenu && optionsWithBackToMain.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {optionsWithBackToMain.map((option) => (
                      <button
                        type="button"
                        key={`${option.label}-${option.categoryId ?? ''}`}
                        onClick={() => handleOptionClick(option)}
                        className="w-full rounded-xl border border-emerald-400/60 bg-[#111b21] px-4 py-2.5 text-left text-xs font-medium text-emerald-300 transition-colors hover:border-emerald-300 hover:bg-emerald-400/10 hover:text-emerald-200"
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}

                {currentMenu === undefined && (
                  <div className="mt-4 flex justify-center">
                    <button
                      type="button"
                      onClick={() => {
                        setConversation([]);
                        setCurrentMenuRef(null);
                        handleInitialize();
                      }}
                      className="text-xs font-medium text-emerald-300 hover:text-emerald-200"
                    >
                      Reiniciar conversa
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="border-t border-white/10 bg-[#111b21] px-4 py-3">
            <div className="rounded-full bg-[#202c33] px-4 py-2 text-xs text-[#8696a0]">
              Digite uma mensagem...
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
